import { Button, Col, Row } from 'antd';
import routePaths from 'config/routes';
import React, { FC, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { customFormatMessage, isFirstScan, useWindowSize } from 'utils';
import { ConsumablesContext } from './consumables';
import ConsumableCoupon, { CouponData } from './consumableCoupon';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { setScanData } from 'src/actions/appActions';
import { connect } from 'react-redux';
import TableSummaryPoints from './TableSummaryPoints';
import { openToast } from '../general/Toast';
import {
  CouponTypeEnum,
  ConsumablePointsOperatorEnum,
  ConsumableNatureEnum,
} from 'src/shared/enums/consumables.enum';
import { IRootReducers } from 'src/reducers';
import redirectTo from 'services/redirectTo';
import Router from 'next/router';
import { refreshUserInfo } from 'src/actions/authActions';
import classnames from 'classnames';
import { config } from 'config/config';

const { ADVOCACY } = CouponTypeEnum;
const { ADD, MULTIPLY } = ConsumablePointsOperatorEnum;

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      setScanData,
      refreshUserInfo,
    },
    dispatch,
  );

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    scanData: state.app.scanData,
  };
};

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const ConsumablesModalPointsSummary: FC<Props> = ({
  setScanData,
  refreshUserInfo,
  scanData,
}): JSX.Element => {
  const {
    setShowModal,
    selectedCustomerCoupon,
    newCoupon,
    welcome,
    isDI,
    skipCoupon,
  } = useContext(ConsumablesContext);

  const { formatMessage } = useIntl();
  const { isMD } = useWindowSize();
  const isMobile = isMD;
  const welcomePoints = 300;

  useEffect(() => {
    if (!welcome) {
      refreshUserInfo();
    }
  }, []);

  const renderTitleUserHasBonus = () => {
    const firstScan = isFirstScan(selectedCustomerCoupon);
    let id: string = 'consumables.points-modal.title.duplicated-points';

    if (selectedCustomerCoupon?.type === ADVOCACY && firstScan === false) {
      id = 'consumables.points-modal.title.extra-points';
    }
    if (selectedCustomerCoupon?.pointsOperator === ADD) {
      id = 'consumables.points-modal.title.extra-points';
    }

    return (
      <Row justify="center">
        <h3 className="consumables-modal-points-summary-content--title">
          {customFormatMessage(id)}
        </h3>
      </Row>
    );
  };

  const renderContentUserLoggedHasBonus = () => {
    let idSubtitle: string =
      'consumables.points-modal.subtitle-{productName}-{treatmentDays}';
    const subTitle = customFormatMessage(idSubtitle, {
      productName: scanData?.consumable?.contents.name,
      treatmentDays: scanData?.consumable?.treatmentDays,
    });
    return (
      <div>
        {renderIcon}
        {renderTitleUserHasBonus()}
        <Row justify="center">
          <p className="consumables-modal-points-summary-content--subtitle">
            {subTitle}
          </p>
        </Row>
        {<TableSummaryPoints />}
        <div className="conusmable-yellow-block-padding">
          {renderPriceInfo()}
        </div>
        <div className="consumables-modal-button-experiences-container">
          <div style={{ width: '100%' }}>{buttonExperiences}</div>
        </div>
      </div>
    );
  };

  const renderContentUserLoggedHasNotBonus = () => {
    let idTitle: string = 'consumables.points-modal.title.won-points-{points}';
    const title = customFormatMessage(idTitle, {
      points: scanData?.newCoupon.consumableScanPoints,
    });
    let idSubtitle: string = 'consumables.points-modal.subtitle-no-coupon';
    const subTitle = customFormatMessage(idSubtitle);
    const firstScan = isFirstScan(newCoupon);
    const couponToShow: CouponData = {
      id: newCoupon.id,
      type: newCoupon.type,
      myCoupons: false,
      firstScan: firstScan,
      bonusPoints: newCoupon.points,
      consumablePoints: newCoupon.consumableScanPoints,
      operation: newCoupon.pointsOperator,
      consumableName: newCoupon.consumable?.contents.name,
      consumableImg: newCoupon.consumable?.contents.image,
      expiration: newCoupon.expiration,
      couponFromLanding: false,
    };

    return (
      <div>
        {renderIcon}
        <Row justify="center">
          <h3 className="consumables-modal-points-summary-content--title">
            {title}
          </h3>
        </Row>
        <Row justify="center">
          <p className="consumables-modal-points-summary-content--subtitle">
            {subTitle}
          </p>
        </Row>
        <Row justify="center" className="consumables-new-di-coupon">
          <ConsumableCoupon {...couponToShow} />
        </Row>
        <div>{renderButtons()}</div>
      </div>
    );
  };

  const renderContentUserNotRegisteredHasNotBonus = () => {
    const points: number = scanData?.userConsumableCoupon?.reduce(
      (acc, coupon) =>
        acc +
        (coupon.type === CouponTypeEnum.ADVOCACY
          ? 0
          : coupon.consumableScanPoints),
      0,
    );

    let idTitle: string =
      'consumables.points-modal.subtitle-no-coupon-scan-DI.welcome';
    if (points > 0)
      idTitle = 'consumables.points-modal.title.won-points-{points}';
    const title = customFormatMessage(idTitle, {
      points: points,
    });

    let idSubtitle: string =
      scanData?.userConsumableCoupon.length === 1
        ? 'consumables.points-modal.subtitle-no-coupon-scan-DI'
        : 'consumables.points-modal.subtitle-no-coupon-scan-DI.various-coupons';
    if (points > 0) {
      if (scanData?.userConsumableCoupon.length === 1) {
        idSubtitle = 'consumables.points-modal.subtitle-no-coupon';
      } else {
        idSubtitle =
          'consumables.points-modal.subtitle-no-coupon.various-coupons';
      }
    }
    const subTitle = customFormatMessage(idSubtitle);

    const buildCoupons = scanData?.userConsumableCoupon.map((coupon) => {
      const couponConsumable = scanData.couponConsumables.find(
        (consumable) => consumable.id === coupon.consumableId,
      );

      if (!couponConsumable) {
        return null;
      }

      const firstScan = isFirstScan(coupon);

      return {
        id: coupon.id,
        type: coupon.type,
        myCoupons: false,
        firstScan: firstScan,
        bonusPoints: coupon.points,
        consumablePoints: coupon.consumableScanPoints,
        operation: coupon.pointsOperator,
        consumableName: scanData?.newCoupon.consumable.contents.name,
        consumableImg: scanData?.newCoupon.consumable.contents.image,
        expiration: coupon.expiration,
        couponFromLanding: false,
      };
    });

    const modalListClasses: string = classnames(
      buildCoupons.length > 1
        ? 'consumables-modal-bonus-list-edge'
        : 'consumables-modal-bonus-list-edge-one',
    );

    return (
      <div>
        {points > 0 && renderIcon}
        <Row justify="center">
          <h3 className="consumables-modal-points-summary-content--title">
            {title}
          </h3>
        </Row>
        <Row justify="center">
          <p className="consumables-modal-points-summary-content--subtitle">
            {subTitle}
          </p>
        </Row>
        <div className={`consumables-new-di-coupon ${modalListClasses}`}>
          {buildCoupons.map((coupon: CouponData) => (
            <div
              key={coupon.id}
              className={`consumables-modal-bonus-coupon ${
                !welcome && 'coupon-cursor-pointer'
              }`}>
              <ConsumableCoupon {...coupon} />
            </div>
          ))}
        </div>

        <div>{renderButtons()}</div>
      </div>
    );
  };

  const renderContentScannedDI = () => {
    const idTitle = welcome
      ? 'consumables.points-modal.subtitle-no-coupon-scan-DI.welcome'
      : 'consumables.points-modal.title.scanned-DI';
    const title = customFormatMessage(idTitle);
    const idSubtitle = 'consumables.points-modal.subtitle-no-coupon-scan-DI';
    const subTitle = customFormatMessage(idSubtitle);
    const firstScan = isFirstScan(newCoupon);
    const couponToShow: CouponData = {
      id: newCoupon.id,
      type: newCoupon.type,
      myCoupons: false,
      firstScan: firstScan,
      bonusPoints: newCoupon.points,
      consumablePoints: newCoupon.consumableScanPoints,
      operation: newCoupon.pointsOperator,
      consumableName: newCoupon.consumable?.contents.name,
      consumableImg: newCoupon.consumable?.contents.image,
      expiration: newCoupon.expiration,
      couponFromLanding: false,
    };

    return (
      <div>
        <Row justify="center">
          <h3 className="consumables-modal-points-summary-content--title-no-icon">
            {title}
          </h3>
        </Row>
        <Row justify="center">
          <p className="consumables-modal-points-summary-content--subtitle">
            {subTitle}
          </p>
        </Row>
        <Row justify="center" className="consumables-new-di-coupon">
          <ConsumableCoupon {...couponToShow} />
        </Row>
        <div>{renderButtons()}</div>
      </div>
    );
  };

  const renderIcon = (
    <Row justify="center">
      <img
        className="points-consumable-top-icon"
        src={`${config.APP.ASSET_PREFIX}/coins_points_consumables.gif`}></img>
    </Row>
  );

  const resetScanData = async () => {
    if (welcome) {
      openToast(
        formatMessage(
          { id: 'notification.points.success' },
          { points: Number(welcomePoints).toLocaleString() },
        ),
        'success',
        6,
        'puntos',
        'increase_50',
      );
    }
    const dataScan = {
      fromLoggedDiScan: false,
      fromLogin: false,
      welcome: false,
      fromScan: false,
      consumable: null,
      userConsumableCoupon: [],
      newCoupon: null,
      qrInfo: null,
    };
    setScanData(dataScan);
  };

  const clickMyCoupons = async () => {
    await setShowModal(false);
    await resetScanData();
    redirectTo(routePaths.PAGES.MY_ACCOUNT.MY_BONUS, undefined, Router);
  };

  const clickExperiences = async () => {
    await setShowModal(false);
    await resetScanData();
    redirectTo(routePaths.PAGES.EXPERIENCE, undefined, Router);
  };

  const buttonMyCoupons = (
    <Row className="points-consumable-button-my-coupons-row">
      <a
        onClick={clickMyCoupons}
        className="points-consumable-button-my-coupons">
        {formatMessage({
          id: 'consumables.points-modal.button.my-coupons',
        })}
      </a>
    </Row>
  );

  const buttonExperiences = (
    <Row className="points-consumale-button-experiences-row">
      <Button
        onClick={clickExperiences}
        className="btn-primary btn-primary--black points-consumable-button-experiences">
        <Row
          justify="center"
          className="points-consumable-button-experiences__content">
          <FormattedMessage id="consumables.points-modal.button.experiences" />
        </Row>
      </Button>
    </Row>
  );

  const renderButtons = () => {
    if (!isMobile) {
      return (
        <Row justify="end">
          <Col span={5}>{buttonMyCoupons}</Col>
          <Col span={10}>{buttonExperiences}</Col>
        </Row>
      );
    } else {
      return (
        <div className="consumables-modal-summary-buttons">
          <Row justify="center">{buttonExperiences}</Row>
          <Row justify="center">{buttonMyCoupons}</Row>
        </div>
      );
    }
  };

  const renderPriceInfo = () => {
    return (
      <div className="consumables-modal-summary-yellow-container">
        <div className="consumable-modal-summary-yellow-ticket-icon">
          <img src={`${config.APP.MEDIA_CON}ticketYellowIcon.png`}></img>
        </div>
        <div>
          <h3 className="consumables-modal-yellow-title">
            {customFormatMessage('consumables.points-modal.yellow-block-title')}
          </h3>
        </div>
        <div>
          <p className="consumables-modal-points-summary-content--subtitle consumables-modal-summary-width">
            {customFormatMessage(
              'consumables.points-modal.yellow-block-subtitle',
            )}
          </p>
        </div>
        <div className="consumable-modal-summary-my-coupons">
          {buttonMyCoupons}
        </div>
      </div>
    );
  };

  return (
    <>
      {welcome && renderContentUserNotRegisteredHasNotBonus()}
      {/* Case isDI */}
      {!welcome && isDI && renderContentScannedDI()}
      {/* Cases user was logged and scanned qr product */}
      {!welcome &&
        scanData?.userConsumableCoupon.length !== 0 &&
        !isDI &&
        !skipCoupon &&
        renderContentUserLoggedHasBonus()}
      {((!welcome && scanData?.userConsumableCoupon.length === 0 && !isDI) ||
        (!welcome && skipCoupon && !isDI)) &&
        renderContentUserLoggedHasNotBonus()}
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConsumablesModalPointsSummary);
